// Der eine Auslöser der Nutzungs-Alerts aus usage-alerts.js. <PricingUsageWatcher />
// steht genau einmal in context/index.js unter PaymentContextProvider, neben
// dem Dialog-Host, und rendert nichts.
//
// Gezählt wird nur, wenn ein Alert überhaupt entstehen darf, also in enforced,
// ohne Admin und ohne Simulation, und nur für Personen, die den Log am
// Partner schreiben dürfen (nicht VIEWER, siehe usage-alerts.js). Sonst
// laufen hier keine Requests. Stellen und Zugänge laden über die leichten
// Queries aus useLimit (fetch 'auto'). Der Talentspeicher liest nur den
// Cache der Talentliste (useTalentStorage({ fetch: false })), die schwere
// Talents-Query startet hier also nie. Ohne Cache gibt es für den Talentspeicher keinen Alert, bis die
// Talentliste einmal geladen hat. Der Cache ist persistiert, ein Alert kann
// also auf einem älteren Stand beruhen. Weil nur Löschen Platz freigibt, war
// der Stand dann zumindest einmal erreicht.
//
// usage-alerts.js wird bewusst zuerst geladen, in einer eigenen Gruppe vor
// context, wie in use-pricing-request.js. Diese Datei hängt über hooks.js im
// Import-Zyklus mit context/index.js.
import {
  describeUsageAlerts,
  isUsageAlertEligible,
  maybeSendUsageAlert,
  usageAlertKey
} from './usage-alerts'

import { useEffect, useMemo, useRef } from 'react'

import { usePartnerContext, useUserContext } from '../../context'
import { useEntitlements, useLimit, useTalentStorage } from './hooks'
import { canWritePartnerLog } from './pricing-slack'

// Nutzung nur, wenn sie fertig gezählt ist. Läuft gerade ein Fetch, wartet
// der Alert auf den frischen Stand.
const countedUsage = (state) =>
  state.status === 'success' &&
  !state.isFetching &&
  !state.pending &&
  typeof state.used === 'number'
    ? state.used
    : null

/**
 * Prüft die Nutzung und schickt fällige Alerts nach #vertrieb. Je Key ein
 * Versuch pro Seitenaufruf. Nach einem Fehler entfernt maybeSendUsageAlert
 * die Markierung, der nächste Seitenaufruf oder ein anderes Teammitglied
 * versucht es erneut. Ein toter Webhook löst so keinen Versuch bei jedem
 * Render aus.
 */
export const useUsageAlerts = () => {
  const entitlements = useEntitlements()
  const { partner } = usePartnerContext()
  const { cognitoUser, isAdmin } = useUserContext()
  const partnerId = partner?.id ?? null

  // Ohne Log-Recht kein Alert, sonst fehlte die Dedupe über alle Nutzer
  const canWriteLog = canWritePartnerLog(cognitoUser, partnerId)
  const eligible =
    canWriteLog && isUsageAlertEligible({ entitlements, isAdmin, partnerId })
  const fetch = eligible ? 'auto' : false

  const careers = useLimit('careers', { fetch })
  const seats = useLimit('seats', { fetch })
  const talentStorage = useTalentStorage({ fetch: false })

  const careersUsed = countedUsage(careers)
  const seatsUsed = countedUsage(seats)
  const talentsUsed = countedUsage(talentStorage)

  const alerts = useMemo(
    () =>
      describeUsageAlerts({
        entitlements,
        isAdmin,
        partner,
        usage: {
          careers: careersUsed,
          seats: seatsUsed,
          talentStorage: talentsUsed
        }
      }),
    [entitlements, isAdmin, partner, careersUsed, seatsUsed, talentsUsed]
  )

  const firedById = cognitoUser?.username ?? null
  const attempted = useRef(new Set())

  useEffect(() => {
    // Ohne Recht wird kein Key als versucht vermerkt, ein später geladenes
    // Recht darf den Alert also noch auslösen
    if (!canWriteLog) return

    alerts.forEach((descriptor) => {
      const key = usageAlertKey(descriptor)
      if (attempted.current.has(key)) return
      attempted.current.add(key)

      maybeSendUsageAlert(descriptor, { canWriteLog, firedById }).catch(() => {
        // Webhook-Fehler melden sendSlackMessage und assertSlackAccepted
        // selbst an Bugsnag
      })
    })
  }, [alerts, canWriteLog, firedById])
}

/** Rendert nichts, siehe useUsageAlerts */
export const PricingUsageWatcher = () => {
  useUsageAlerts()
  return null
}
